import { useState, useEffect, useRef, useCallback } from 'react';
import { RTVIClient, RTVIEvent } from '@pipecat-ai/client-js';
import { WebSocketTransport } from '@pipecat-ai/websocket-transport';
import Icon from './atoms/Icon';

const FeatureChip = ({ icon, title, desc }) => (
  <div className="flex items-center gap-2.5 bg-white rounded-lg px-3 py-2 border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all w-full">
    <div className="w-7 h-7 bg-blue-50 rounded-md flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size={15} className="text-[#2b6cee]" />
    </div>
    <div className="min-w-0">
      <h4 className="text-gray-800 font-semibold text-[11px] leading-tight">{title}</h4>
      <p className="text-gray-400 text-[10px] leading-tight">{desc}</p>
    </div>
  </div>
);

const LightningLine = ({ delay = 0, reverse = false, color = 'indigo' }) => {
  const colors = color === 'green'
    ? { base: '#bbf7d0', flash: '#22c55e', spark: '#16a34a', core: '#86efac' }
    : color === 'blue'
    ? { base: '#bfdbfe', flash: '#3b82f6', spark: '#2563eb', core: '#93c5fd' }
    : { base: '#e0e7ff', flash: '#818cf8', spark: '#4f46e5', core: '#c7d2fe' };

  return (
    <svg className="w-full h-5 flex-shrink" viewBox="0 0 200 20" preserveAspectRatio="none">
      <path d="M0,10 L25,5 L50,15 L75,5 L100,15 L125,5 L150,15 L175,5 L200,10"
            fill="none" stroke={colors.base} strokeWidth="1" strokeLinecap="round" />
      <path d="M0,10 L25,5 L50,15 L75,5 L100,15 L125,5 L150,15 L175,5 L200,10"
            fill="none" stroke={colors.flash} strokeWidth="2.5" strokeLinecap="round"
            opacity="0" style={{ filter: `drop-shadow(0 0 5px ${colors.flash}99)` }}>
        <animate attributeName="opacity" values="0;0;0;0.6;0" dur="4s" begin={`${delay + 1}s`} repeatCount="indefinite" />
      </path>
      <path d="M0,10 L25,5 L50,15 L75,5 L100,15 L125,5 L150,15 L175,5 L200,10"
            fill="none" stroke={colors.spark} strokeWidth="2" strokeLinecap="round"
            strokeDasharray="30 170"
            style={{ filter: `drop-shadow(0 0 4px ${colors.spark}ee)` }}>
        <animate attributeName="stroke-dashoffset"
                 from={reverse ? '0' : '200'} to={reverse ? '200' : '0'}
                 dur="1.6s" begin={`${delay}s`} repeatCount="indefinite" />
      </path>
      <path d="M0,10 L25,5 L50,15 L75,5 L100,15 L125,5 L150,15 L175,5 L200,10"
            fill="none" stroke={colors.core} strokeWidth="1" strokeLinecap="round"
            strokeDasharray="14 186"
            style={{ filter: `drop-shadow(0 0 8px ${colors.core})` }}>
        <animate attributeName="stroke-dashoffset"
                 from={reverse ? '0' : '200'} to={reverse ? '200' : '0'}
                 dur="1.6s" begin={`${delay}s`} repeatCount="indefinite" />
      </path>
    </svg>
  );
};

// The full 5-row grid with feature cards, lightning lines, and center mic
const FeatureGrid = ({ leftFeatures, rightFeatures, centerContent, lightningColor = 'indigo' }) => (
  <>
    {/* Desktop */}
    <div className="hidden md:block w-full max-w-4xl mx-auto">
      <div className="relative">
        {/* Center content - absolutely positioned */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center">
          {centerContent}
        </div>

        {/* Feature rows */}
        <div className="flex flex-col gap-2.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center">
              <div className="w-[165px] flex-shrink-0">
                <FeatureChip icon={leftFeatures[i].icon} title={leftFeatures[i].title} desc={leftFeatures[i].desc} />
              </div>
              <div className="flex-1 px-1">
                <LightningLine delay={i * 0.3} color={lightningColor} />
              </div>
              <div className="w-24 flex-shrink-0" />
              <div className="flex-1 px-1">
                <LightningLine delay={i * 0.3 + 0.15} reverse color={lightningColor} />
              </div>
              <div className="w-[165px] flex-shrink-0">
                <FeatureChip icon={rightFeatures[i].icon} title={rightFeatures[i].title} desc={rightFeatures[i].desc} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Mobile */}
    <div className="md:hidden flex flex-col items-center gap-4 w-full">
      <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
        {[...leftFeatures, ...rightFeatures].map((f, i) => (
          <FeatureChip key={i} icon={f.icon} title={f.title} desc={f.desc} />
        ))}
      </div>
      {centerContent}
    </div>
  </>
);

const InPageVoiceDemo = () => {
  const [demoState, setDemoState] = useState('idle');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [isMicEnabled, setIsMicEnabled] = useState(false);

  const rtviClientRef = useRef(null);
  const sessionIdRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioElementRef = useRef(null);
  const sectionRef = useRef(null);

  const leftFeatures = [
    { icon: 'smart_toy', title: 'AI Interviews', desc: 'Automated screening' },
    { icon: 'analytics', title: 'Smart Analytics', desc: 'Data-driven insights' },
    { icon: 'speed', title: 'Save 80% Time', desc: 'Faster hiring' },
    { icon: 'verified', title: 'Skill Assessment', desc: 'Real-time evaluation' },
    { icon: 'description', title: 'Resume Parsing', desc: 'Auto-extract skills' },
  ];

  const rightFeatures = [
    { icon: 'videocam', title: 'Live Proctoring', desc: 'Interview monitoring' },
    { icon: 'assessment', title: 'PDF Reports', desc: 'Detailed scoring' },
    { icon: 'schedule', title: '24/7 Available', desc: 'Always-on AI' },
    { icon: 'balance', title: 'Bias-Free', desc: 'Fair evaluation' },
    { icon: 'integration_instructions', title: 'Easy Integration', desc: 'Quick setup' },
  ];

  useEffect(() => {
    return () => {
      if (rtviClientRef.current) {
        try { rtviClientRef.current.disconnect(); } catch (e) { /* ignore */ }
        rtviClientRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  const getApiBaseUrl = () => {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:8000`;
    }
    const backendUrl = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
    return backendUrl.replace('/api', '');
  };

  const setupAudioTrack = useCallback((track) => {
    if (audioElementRef.current) {
      const stream = new MediaStream([track]);
      audioElementRef.current.srcObject = stream;
      audioElementRef.current.play().catch(e => console.error('Audio play failed:', e));
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartDemo = async () => {
    if (!validateForm()) return;
    setConnectionError(null);
    setDemoState('connecting');
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    try {
      sessionIdRef.current = `hr-voice-${Date.now()}`;
      audioContextRef.current = new AudioContext();
      await audioContextRef.current.resume();

      const params = new URLSearchParams({
        session_id: sessionIdRef.current,
        visitor_name: formData.name,
        visitor_email: formData.email,
        visitor_phone: formData.phone || '',
        language: 'en-US',
        duration_minutes: '10'
      });

      const transport = new WebSocketTransport();
      const rtviConfig = {
        transport,
        params: {
          baseUrl: getApiBaseUrl(),
          endpoints: { connect: `/api/hr-voice-assistant/connect?${params.toString()}` },
        },
        enableMic: true,
        enableCam: false,
        callbacks: {
          onConnected: () => { setDemoState('connected'); setIsListening(true); },
          onDisconnected: () => { setDemoState('ended'); setIsListening(false); setIsSpeaking(false); },
          onBotReady: () => {
            const tracks = rtviClientRef.current?.tracks();
            if (tracks?.bot?.audio) setupAudioTrack(tracks.bot.audio);
          },
          onGenericMessage: () => {},
          onMessageError: (error) => console.error('Message error:', error),
          onError: (error) => {
            console.error('Connection error:', error);
            setConnectionError('Connection error. Please try again.');
            setDemoState('form');
          },
        },
      };

      const client = new RTVIClient(rtviConfig);
      rtviClientRef.current = client;

      client.on(RTVIEvent.TrackStarted, (track, participant) => {
        if (!participant?.local && track.kind === 'audio') {
          setupAudioTrack(track);
          setIsSpeaking(true);
          setIsListening(false);
        }
      });
      client.on(RTVIEvent.TrackStopped, (track, participant) => {
        if (!participant?.local && track.kind === 'audio') {
          setIsSpeaking(false);
          setIsListening(true);
        }
      });

      await client.initDevices();
      const localTracks = client.tracks().local;
      if (localTracks?.audio) {
        localTracks.audio.enabled = true;
        setIsMicEnabled(true);
        setIsListening(true);
      }
      await client.connect();
    } catch (error) {
      console.error('Failed to start voice assistant:', error);
      setConnectionError('Failed to connect. Please try again.');
      setDemoState('form');
      if (rtviClientRef.current) {
        try { await rtviClientRef.current.disconnect(); } catch (e) { /* ignore */ }
      }
    }
  };

  const handleEndCall = async () => {
    if (rtviClientRef.current) {
      try {
        await rtviClientRef.current.disconnect();
        rtviClientRef.current = null;
        if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }
      } catch (e) { console.error('Error disconnecting:', e); }
    }
    setDemoState('ended');
  };

  const handleReset = () => {
    setDemoState('idle');
    setFormData({ name: '', email: '', phone: '' });
    setErrors({});
    setConnectionError(null);
    setIsSpeaking(false);
    setIsListening(false);
    setIsMicEnabled(false);
  };

  // Determine lightning color based on state
  const getLightningColor = () => {
    if (demoState === 'connected') {
      if (isSpeaking) return 'green';
      if (isListening) return 'blue';
    }
    return 'indigo';
  };

  // Build center content based on state
  const buildCenterContent = () => {
    if (demoState === 'idle') {
      return (
        <>
          <button
            onClick={() => setDemoState('form')}
            className="relative w-[72px] h-[72px] bg-gradient-to-br from-[#4f46e5] to-[#2b6cee] rounded-full shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center cursor-pointer group animate-micGlow"
          >
            <div className="absolute inset-0 rounded-full bg-indigo-500/30 animate-ping"></div>
            <Icon name="mic" size={30} className="text-white relative z-10 group-hover:scale-110 transition-transform" />
          </button>
          <span className="text-gray-400 text-[10px] font-medium mt-1.5 whitespace-nowrap">Click to Start Demo</span>
        </>
      );
    }

    if (demoState === 'connecting') {
      return (
        <div className="flex flex-col items-center">
          <div className="w-[72px] h-[72px] bg-indigo-50 rounded-full flex items-center justify-center">
            <div className="animate-spin">
              <Icon name="sync" size={30} className="text-[#4f46e5]" />
            </div>
          </div>
          <span className="text-gray-500 text-[10px] font-semibold mt-1.5 whitespace-nowrap">Connecting...</span>
        </div>
      );
    }

    if (demoState === 'connected') {
      return (
        <div className="flex flex-col items-center">
          {/* Mic circle with pulse effects */}
          <div className="relative flex items-center justify-center">
            {isSpeaking && (
              <>
                <div className="absolute w-[100px] h-[100px] bg-green-400/15 rounded-full animate-ping"></div>
                <div className="absolute w-[90px] h-[90px] bg-green-400/20 rounded-full animate-pulse"></div>
              </>
            )}
            {isListening && !isSpeaking && (
              <>
                <div className="absolute w-[100px] h-[100px] bg-blue-400/15 rounded-full animate-ping"></div>
                <div className="absolute w-[90px] h-[90px] bg-blue-400/20 rounded-full animate-pulse"></div>
              </>
            )}
            <div className={`relative w-[72px] h-[72px] rounded-full flex items-center justify-center transition-all shadow-lg ${
              isSpeaking ? 'bg-gradient-to-br from-green-400 to-green-600' :
              isListening ? 'bg-gradient-to-br from-blue-400 to-blue-600' :
              'bg-gradient-to-br from-gray-400 to-gray-500'
            }`}>
              <Icon name="mic" size={30} className="text-white" />
            </div>
          </div>

          {/* Status text */}
          <div className="mt-1.5">
            {isSpeaking && (
              <div className="flex items-center gap-1.5 text-green-600">
                <div className="flex gap-[2px]">
                  {[0, 80, 160, 240, 320].map((d) => (
                    <div key={d} className="w-[3px] h-3 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: `${d}ms` }} />
                  ))}
                </div>
                <span className="font-semibold text-[10px] whitespace-nowrap">AI Speaking...</span>
              </div>
            )}
            {isListening && !isSpeaking && (
              <div className="flex items-center gap-1 text-blue-600">
                <Icon name="hearing" size={12} />
                <span className="font-semibold text-[10px] whitespace-nowrap">Listening...</span>
              </div>
            )}
            {!isSpeaking && !isListening && (
              <span className="font-semibold text-[10px] text-gray-500 whitespace-nowrap">Connected</span>
            )}
          </div>

          {/* End call button */}
          <button onClick={handleEndCall}
            className="mt-2 bg-red-500 hover:bg-red-600 text-white font-semibold px-4 py-1.5 rounded-full transition-colors flex items-center gap-1.5 shadow-md text-[11px]">
            <Icon name="call_end" size={14} />
            End Call
          </button>
        </div>
      );
    }

    if (demoState === 'ended') {
      return (
        <div className="flex flex-col items-center">
          <div className="w-[72px] h-[72px] bg-green-50 rounded-full flex items-center justify-center">
            <Icon name="check_circle" size={30} className="text-green-500" />
          </div>
          <span className="text-gray-600 text-[10px] font-semibold mt-1.5 whitespace-nowrap">Call Ended</span>
          <button onClick={handleReset}
            className="mt-2 bg-gradient-to-r from-[#4f46e5] to-[#2b6cee] hover:from-[#4338ca] hover:to-[#1a4fb8] text-white font-semibold px-4 py-1.5 rounded-full transition-all text-[11px] shadow-md">
            Try Again
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <section ref={sectionRef} className="relative py-10 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Subtle background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-blue-100/40 via-indigo-100/30 to-purple-100/20 blur-3xl"></div>
      </div>

      <style>{`
        @keyframes scaleInDemo {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scaleInDemo { animation: scaleInDemo 0.35s ease-out forwards; }
        @keyframes micGlow {
          0%, 100% { box-shadow: 0 0 20px 4px rgba(79,70,229,0.15); }
          50% { box-shadow: 0 0 30px 8px rgba(79,70,229,0.25); }
        }
        .animate-micGlow { animation: micGlow 2.5s ease-in-out infinite; }
      `}</style>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6">
          <p className="text-xs font-semibold text-[#2b6cee] uppercase tracking-wider mb-1.5">
            Experience AI Voice Assistant
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Talk to Our AI — Right Here, Right Now
          </h2>
          <p className="text-sm text-gray-500 max-w-lg mx-auto">
            {demoState === 'connected'
              ? (isSpeaking ? 'The AI assistant is speaking. Listen carefully!' : 'Speak now to ask about Aptirix')
              : 'Click the mic to start a live voice conversation with our AI recruiter assistant'}
          </p>
        </div>

        {/* Central demo area - ALWAYS shows the grid with cards */}
        <div className="relative flex items-center justify-center min-h-[260px] md:min-h-[290px]">
          {/* Form overlay - shown on top of grid when in form state */}
          {demoState === 'form' && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60 backdrop-blur-sm rounded-xl">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-sm mx-4 p-5 animate-scaleInDemo">
                <button
                  onClick={() => { setDemoState('idle'); setErrors({}); setConnectionError(null); }}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors z-10"
                  style={{ position: 'relative' }}
                >
                  <Icon name="close" size={18} />
                </button>

                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-9 h-9 bg-indigo-50 rounded-full flex items-center justify-center">
                    <Icon name="mic" size={20} className="text-[#4f46e5]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Start Voice Demo</h3>
                    <p className="text-gray-400 text-xs">Enter your details to begin</p>
                  </div>
                </div>

                {connectionError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 flex items-start gap-2 mb-3">
                    <Icon name="error" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-600 text-xs">{connectionError}</p>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Name <span className="text-red-400">*</span></label>
                    <input type="text" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.name ? 'border-red-400' : 'border-gray-200'}`}
                      placeholder="Your name" />
                    {errors.name && <p className="text-red-400 text-[10px] mt-0.5">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email <span className="text-red-400">*</span></label>
                    <input type="email" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.email ? 'border-red-400' : 'border-gray-200'}`}
                      placeholder="your@email.com" />
                    {errors.email && <p className="text-red-400 text-[10px] mt-0.5">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Phone (Optional)</label>
                    <input type="tel" value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="+1 (555) 000-0000" />
                  </div>
                  <button onClick={handleStartDemo}
                    className="w-full bg-gradient-to-r from-[#4f46e5] to-[#2b6cee] hover:from-[#4338ca] hover:to-[#1a4fb8] text-white font-semibold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 text-sm shadow-md">
                    <Icon name="mic" size={16} />
                    Start Voice Demo
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* The grid is ALWAYS visible */}
          <FeatureGrid
            leftFeatures={leftFeatures}
            rightFeatures={rightFeatures}
            centerContent={buildCenterContent()}
            lightningColor={getLightningColor()}
          />
        </div>
      </div>

      <audio ref={audioElementRef} autoPlay playsInline className="hidden" />
    </section>
  );
};

export default InPageVoiceDemo;
